import type { ButtonProps } from '@ui/Button';

import { forwardRef } from 'react';

import useClassy from '@core/hooks/useClassy';
import { useSuperHubStore } from '@core/store';
import getVersionBranchBase from '@core/utils/i18n/getVersionBranchBase';
import Button from '@ui/Button';
import Icon from '@ui/Icon';

import { useCreateBranchMetaContext } from './Context';
import styles from './index.module.scss';

const CreateBranchButton = forwardRef<HTMLButtonElement, ButtonProps>(function CreateBranchButton(
  { children, circular = true, onClick, ...rest },
  ref,
) {
  const bem = useClassy(styles, 'CreateBranch');

  const { version, source } = useCreateBranchMetaContext();
  const [updateEphemeralBranch, isI18nEnabled] = useSuperHubStore(s => [
    s.branches.updateEphemeralBranch,
    s.isI18nEnabled,
  ]);

  // Lang-aware base — see `getVersionBranchBase`. Without this a branch
  // created from a translation version would be missing the lang suffix
  // entirely (e.g. `1.0_foo` instead of `1.0-fr_foo`); the API would then
  // file the branch under the English base.
  const base = getVersionBranchBase(version, isI18nEnabled) ?? version.name;

  return (
    <Button
      ref={ref}
      aria-label="Create Branch"
      circular={circular}
      className={bem('-button')}
      kind="secondary"
      onClick={e => {
        updateEphemeralBranch({ base, source });
        onClick?.(e);
      }}
      size="xs"
      soft
      text
      uppercase={false}
      {...rest}
    >
      {children || (
        <>
          <Icon aria-hidden="true" name="plus" />
          <span>Branch</span>
        </>
      )}
    </Button>
  );
});

export default CreateBranchButton;
